import React, { useMemo, useState, useRef, useCallback, useEffect } from "react"
import { Link } from "react-router-dom"
import { fetchWheelMovies, findMovieById } from "../services/movieService"
import { useEmotionFilter } from "../context/EmotionFilterContext"
import EmotionBarcode from "./EmotionBarcode"
import "./MovieSpaceWheel.css"

const EMOTION_COLORS = {
  happy: "#E7C239", amused: "#D93D57", involved: "#E34327", inspired: "#EC9965",
  tender: "#E65C9E", surprise: "#53A7BD", astonished: "#59A8C9", sad: "#3374B4",
  melancholic: "#3374B4", bored: "#95B6D8", compassioned: "#A8D4EA", fear: "#5BAF61",
  disturbed: "#418266", scared: "#428249", disgust: "#7556A5", disappointed: "#9678B6",
  embarrassed: "#3374B4", angry: "#975767", annoyed: "#D096A2", nothing: "#333333",
}

const POOL_SIZE = 156
const PAGE_SIZE = 60

let _uid = 0
const ingest = (movie) => ({ ...movie, uid: ++_uid })

// Archimedean spiral: a single thread with a beginning (centre = newest films)
// and an end (outer rim = oldest films, which drain off the wheel).
const buildSpiral = (movies) => {
  const b = 8.5           // radius growth per radian (lower = tighter coil)
  const spacing = 23      // target distance between consecutive bubbles
  const startTheta = 3.4  // leave room for the centre + button

  const pts = []
  let theta = startTheta
  for (let i = 0; i < movies.length; i++) {
    pts.push({ theta, r: b * theta })
    theta += spacing / (b * theta) // step that keeps ~constant spacing
  }

  const outerR = pts.length ? pts[pts.length - 1].r : b * startTheta
  const pad = 30
  const size = 2 * (outerR + 22 + pad)
  const cx = size / 2, cy = size / 2

  const nodes = movies.map((m, i) => {
    const { theta: th, r } = pts[i]
    return {
      ...m,
      x: cx + r * Math.cos(th),
      y: cy + r * Math.sin(th),
      r: 4 + (m.intensity || 3) * 2.0,
      theta: th,
      radius: r,
    }
  })

  // faint spiral thread
  let d = ""
  for (let t = startTheta; t <= theta; t += 0.12) {
    const rr = b * t
    const x = cx + rr * Math.cos(t)
    const y = cy + rr * Math.sin(t)
    d += (d ? " L " : "M ") + x.toFixed(1) + " " + y.toFixed(1)
  }

  return { nodes, size, cx, cy, path: d }
}

export default function MovieSpaceWheel({ onSelectMovie, period = null }) {
  const { emotion: filterEmotion, clear } = useEmotionFilter()

  const [movies, setMovies] = useState([])
  const [loading, setLoading] = useState(true)
  const [spinning, setSpinning] = useState(false)
  const [fresh, setFresh] = useState(new Set())
  const [selected, setSelected] = useState(null)
  const [details, setDetails] = useState(null)
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [angle, setAngle] = useState(0)

  const angleRef = useRef(0)
  const pageRef = useRef(1)
  const hasMoreRef = useRef(true)
  const filterRef = useRef(filterEmotion)
  const periodRef = useRef(period)

  // (re)load whenever the emotion filter OR the Time Machine period changes
  useEffect(() => {
    let alive = true
    filterRef.current = filterEmotion
    periodRef.current = period
    setLoading(true)
    setSelected(null)
    setDetails(null)
    fetchWheelMovies(1, POOL_SIZE, filterEmotion, period)
      .then((data) => {
        if (!alive) return
        setMovies((data.movies || []).map(ingest))
        pageRef.current = data.page || 1
        hasMoreRef.current = !!data.hasMore
      })
      .catch(() => alive && setMovies([]))
      .finally(() => alive && setLoading(false))
    return () => { alive = false }
  }, [filterEmotion, period?.year, period?.month])

  const { nodes, size, cx, cy, path } = useMemo(() => buildSpiral(movies), [movies])

  const loadMore = useCallback(async () => {
    if (spinning) return
    setSpinning(true)
    angleRef.current += 24
    setAngle(angleRef.current)

    const next = hasMoreRef.current ? pageRef.current + 1 : 1
    let incoming = []
    try {
      const data = await fetchWheelMovies(next, PAGE_SIZE, filterRef.current, periodRef.current)
      incoming = (data.movies || []).map(ingest)
      pageRef.current = data.page || next
      hasMoreRef.current = !!data.hasMore
    } catch {
      incoming = []
    }

    if (incoming.length) {
      setFresh(new Set(incoming.map((m) => m.uid)))
      // new films born at the centre; the oldest (tail) drain off the rim
      setMovies((prev) => [...incoming, ...prev].slice(0, POOL_SIZE))
    }

    setTimeout(() => {
      setSpinning(false)
      setFresh(new Set())
    }, 850)
  }, [spinning])

  const pick = (n, e) => {
    e.stopPropagation()
    if (selected?.uid === n.uid) { setSelected(null); setDetails(null); return }
    setSelected(n)
    setDetails(null)
    setDetailsLoading(true)
    if (onSelectMovie) onSelectMovie(n)
    findMovieById(n.id)
      .then((m) => setDetails(m))
      .catch(() => setDetails(null))
      .finally(() => setDetailsLoading(false))
  }

  const closePanel = () => { setSelected(null); setDetails(null) }

  const color = filterEmotion ? EMOTION_COLORS[filterEmotion] : null

  if (loading) {
    return <div className="msw"><p className="msw__hint">Loading the wheel…</p></div>
  }
  if (!movies.length) {
    return (
      <div className="msw">
        <p className="msw__hint">
          {filterEmotion
            ? `No “${filterEmotion}” films found for this period.`
            : period
            ? "No films found for this month."
            : <>No movies yet — <Link to="/search">search a film</Link> to populate the wheel.</>}
        </p>
      </div>
    )
  }

  return (
    <div className="msw" onClick={closePanel}>
      <div className="msw__scene">
        <svg viewBox={`0 0 ${size} ${size}`} className="msw__svg">
          {/* one-shot rotation of the whole spiral on + */}
          <g
            className={`msw__spiral ${spinning ? "is-spinning" : ""}`}
            style={{ transform: `rotate(${angle}deg)`, transformOrigin: `${cx}px ${cy}px` }}
          >
            <path d={path} className="msw__thread" />

            {nodes.map((n) => (
              <circle
                key={n.uid}
                cx={n.x}
                cy={n.y}
                r={n.r}
                fill={EMOTION_COLORS[n.emotion] || "#95B6D8"}
                className={[
                  "msw__bubble",
                  selected?.uid === n.uid ? "is-selected" : "",
                  fresh.has(n.uid) ? "is-fresh" : "",
                ].join(" ").trim()}
                style={{ filter: `saturate(${((n.intensity || 3) / 5) * 100 + 30}%)` }}
                onClick={(e) => pick(n, e)}
              >
                <title>{n.title} · {n.emotion}</title>
              </circle>
            ))}
          </g>

          {/* centre is ALWAYS the + (load more) — never covered by selection */}
          <g
            className="msw__center"
            onClick={(e) => { e.stopPropagation(); loadMore() }}
          >
            <circle
              cx={cx} cy={cy} r="17"
              className="msw__center-bg"
              style={filterEmotion ? { fill: color, stroke: color } : {}}
            />
            <line x1={cx - 6} y1={cy} x2={cx + 6} y2={cy} className="msw__plus-line" style={filterEmotion ? { stroke: "#fff" } : {}} />
            <line x1={cx} y1={cy - 6} x2={cx} y2={cy + 6} className="msw__plus-line" style={filterEmotion ? { stroke: "#fff" } : {}} />
          </g>
        </svg>

        {/* info card — search-page fields + emotion barcode */}
        <div
          className={`msw__panel ${selected ? "msw__panel--visible" : ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          {selected && (
            <>
              <button className="msw__panel-close" onClick={closePanel}>✕</button>

              {details?.poster_path && (
                <img
                  className="msw__panel-poster"
                  src={`https://image.tmdb.org/t/p/w185/${details.poster_path}`}
                  alt={details.title}
                />
              )}

              <p className="msw__panel-emotion">{selected.emotion}</p>
              <h3 className="msw__panel-title">{details?.title || selected.title}</h3>

              {detailsLoading && <p className="msw__panel-loading">Loading details…</p>}

              {details && (
                <div className="msw__panel-meta">
                  {details.director && <p><span>Director</span>{details.director}</p>}
                  {details.genre && <p><span>Genre</span>{details.genre}</p>}
                  {details.country && <p><span>Country</span>{details.country}</p>}
                  {details.duration ? <p><span>Duration</span>{details.duration} min</p> : null}
                </div>
              )}

              <EmotionBarcode
                emotion={selected.emotion}
                summary={details?.emotionSummary}
                seed={selected.id}
              />

              <div className="msw__panel-actions">
                <Link to={`/movie/${selected.id}`} className="msw__panel-btn" onClick={(e) => e.stopPropagation()}>
                  View Profile
                </Link>
                <Link to={`/classify/${selected.id}`} className="msw__panel-btn msw__panel-btn--outline" onClick={(e) => e.stopPropagation()}>
                  Classify
                </Link>
              </div>
            </>
          )}
        </div>
      </div>

      {filterEmotion ? (
        <button
          className="msw__clear-filter"
          onClick={(e) => { e.stopPropagation(); clear() }}
        >
          <span className="msw__clear-dot" style={{ background: color }} />
          Showing <strong>{filterEmotion}</strong> · ✕ back to all emotions
        </button>
      ) : (
        <p className="msw__hint">Click a film to explore · <span>+</span> for more</p>
      )}
    </div>
  )
}
